import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { v4 as uuidv4 } from "uuid";

import { chatService } from "modules/cstIasp/services/chatService";
import { createChatSocket, ChatSocket } from "modules/cstIasp/services/chatSocketService";
import { getWorkbookfromJson } from "core/hooks/jsonToCsv";

const MODEL_ID =
  (process.env.REACT_APP_PENTESTAUTOMATION_MODEL as string) || "api-pentest";

type ChatPageProps = {
  conversationId: string;
  onConversationIdChange: (conversationId: string) => void;
};

type UiChatMessage = {
  role: "user" | "assistant" | "system";
  content: string;
  chatid?: string;
};

const ChatPage: React.FC<ChatPageProps> = ({
  conversationId,
  onConversationIdChange,
}) => {
  const [messages, setMessages] = useState<UiChatMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [socket, setSocket] = useState<ChatSocket | null>(null);

  const streamIdleTimeoutRef = useRef<number | null>(null);
  const titledConversationIdsRef = useRef<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const extractExcelBlock = (content: string): {
    before: string;
    excelBlock: string;
    after: string;
  } | null => {
    if (typeof content !== "string") return null;
    const openTag = "<excel>";
    const closeTag = "</excel>";
    const start = content.indexOf(openTag);
    if (start < 0) return null;

    const end = content.indexOf(closeTag, start + openTag.length);
    if (end >= 0) {
      const excelBlock = content.slice(start, end + closeTag.length);
      return {
        before: content.slice(0, start),
        excelBlock,
        after: content.slice(end + closeTag.length),
      };
    }

    const raw = content.slice(start + openTag.length);
    const excelBlock = `${openTag}${raw}${closeTag}`;
    return {
      before: content.slice(0, start),
      excelBlock,
      after: "",
    };
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const s = createChatSocket();
    setSocket(s);

    const onStreamMessage = (raw: unknown) => {
      const text = typeof raw === "string" ? raw : JSON.stringify(raw);
      let payload: any;
      try {
        payload = JSON.parse(text);
      } catch {
        payload = { content: text };
      }

      const chatid: string | undefined = payload?.chatid ?? payload?.id;
      const chunk: string = payload?.content ?? "";
      if (!chunk) return;

      setMessages((prev) => {
        if (!chatid) {
          return [...prev, { role: "assistant", content: chunk }];
        }

        const lastIdx = [...prev]
          .map((m, idx) => ({ m, idx }))
          .reverse()
          .find(({ m }) => m.role === "assistant" && m.chatid === chatid)?.idx;

        if (lastIdx === undefined) {
          return [...prev, { role: "assistant", content: chunk, chatid }];
        }

        const next = [...prev];
        next[lastIdx] = {
          ...next[lastIdx],
          content: (next[lastIdx].content ?? "") + chunk,
        };
        return next;
      });

      if (streamIdleTimeoutRef.current) {
        window.clearTimeout(streamIdleTimeoutRef.current);
      }
      streamIdleTimeoutRef.current = window.setTimeout(() => {
        setIsLoading(false);
      }, 800);
    };

    s.on("message", onStreamMessage);

    return () => {
      if (streamIdleTimeoutRef.current) {
        window.clearTimeout(streamIdleTimeoutRef.current);
      }
      s.off("message", onStreamMessage);
      s.disconnect();
      setSocket(null);
    };
  }, []);

  useEffect(() => {
    const normalizedConversationId = (conversationId || "").trim();
    if (!normalizedConversationId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const normalizeContent = (content: any): string => {
      if (typeof content === "string") return content;
      if (content && typeof content === "object") {
        if (typeof content.name === "string") return content.name;
        try {
          return JSON.stringify(content);
        } catch {
          return String(content);
        }
      }
      return content == null ? "" : String(content);
    };

    chatService
      .getConversationViaApiConversations({ conversationid: normalizedConversationId })
      .then((conversation) => {
        if (cancelled) return;

        if (!conversation) {
          setMessages([]);
          return;
        }

        titledConversationIdsRef.current.add(normalizedConversationId);

        const history = Array.isArray(conversation.messages)
          ? conversation.messages
          : [];
        const nextMessages = history.map((m: any) => ({
          role: (m?.role as "user" | "assistant" | "system") || "assistant",
          content: normalizeContent(m?.content),
          chatid: typeof m?.chatid === "string" ? m.chatid : undefined,
        }));

        setMessages(nextMessages);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load conversation history:", error);
        setMessages([
          {
            role: "assistant",
            content: "Failed to load conversation history. Please try again.",
          },
        ]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const ensureConversationId = (): string => {
    if (conversationId && conversationId.trim() !== "") return conversationId;
    const generated = uuidv4();
    onConversationIdChange(generated);
    localStorage.setItem("pentestAutomation_conversationid", generated);
    return generated;
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    if (!socket) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Chat socket is not connected. Please refresh and try again.",
        },
      ]);
      return;
    }

    const conversationid = ensureConversationId();
    const isNewConversation = messages.length === 0;
    const trimmed = input.trim();

    const userID = uuidv4();
    const assistantID = uuidv4();
    const chatid = `${userID}:${assistantID}`;

    setMessages((prev) => [
      ...prev,
      { role: "user", content: trimmed, chatid },
      { role: "assistant", content: "", chatid },
    ]);
    setInput("");
    setIsLoading(true);

    if (isNewConversation && !titledConversationIdsRef.current.has(conversationid)) {
      const title = trimmed.length > 30 ? trimmed.substring(0, 30) : trimmed;
      titledConversationIdsRef.current.add(conversationid);
      chatService
        .updateConversationTitleViaApiConversations({
          conversationid,
          conversationTitle: title,
        })
        .catch(() => {
          titledConversationIdsRef.current.delete(conversationid);
        });
    }

    try {
      const reqData = {
        content: trimmed,
        model: MODEL_ID,
        conversationid,
        id: chatid,
      };
      socket.emit("message", JSON.stringify(reqData));
    } catch (error) {
      console.error("Socket send error:", error);
      setIsLoading(false);
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "assistant",
          content: "Sorry, an error occurred. Please try again.",
          chatid,
        },
      ]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Card sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <CardContent
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography>Start a conversation with the AI assistant</Typography>
            </Box>
          )}

          {messages.map((message, index) => (
            <Paper
              key={index}
              sx={{
                p: 2,
                maxWidth: "80%",
                alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                bgcolor: message.role === "user" ? "primary.main" : "grey.100",
                color: message.role === "user" ? "white" : "text.primary",
              }}
            >
              {message.role === "assistant" ? (
                (() => {
                  const excelParts = extractExcelBlock(message.content || "");
                  if (excelParts) {
                    const before = excelParts.before?.trim();
                    const after = excelParts.after?.trim();
                    return (
                      <Box>
                        {before ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {before}
                          </ReactMarkdown>
                        ) : null}

                        <Box sx={{ mt: before ? 1 : 0, mb: after ? 1 : 0 }}>
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={async (e) => {
                              e.preventDefault();
                              const ok = await getWorkbookfromJson(excelParts.excelBlock);
                              if (!ok) console.error("Failed to parse <excel> content");
                            }}
                            onKeyDown={async (e) => {
                              if (e.key !== "Enter" && e.key !== " ") return;
                              e.preventDefault();
                              const ok = await getWorkbookfromJson(excelParts.excelBlock);
                              if (!ok) console.error("Failed to parse <excel> content");
                            }}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: "pointer",
                              px: 1,
                              py: 0.75,
                              borderRadius: 1,
                              bgcolor: "background.paper",
                              border: 1,
                              borderColor: "divider",
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Download Excel Report
                            </Typography>
                          </Box>
                        </Box>

                        {after ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {after}
                          </ReactMarkdown>
                        ) : null}
                      </Box>
                    );
                  }

                  return (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content || "..."}
                    </ReactMarkdown>
                  );
                })()
              ) : (
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{message.content}</Typography>
              )}
            </Paper>
          ))}

          {isLoading && messages[messages.length - 1]?.content === "" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Thinking...
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            sx={{ bgcolor: "background.paper" }}
          />

          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            sx={{ alignSelf: "flex-end" }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Card>
    </Box>
  );
};

export default ChatPage;
